import { useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import {
  faCheckCircle,
  faPencil,
  faTimesCircle,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NumericFormat } from 'react-number-format';
import { CircularProgress } from '@mui/material';
import classNames from 'classnames';

function CommonTextInput({
  label,
  initialValue,
  labelClassName,
  type,
  maxLength,
  readOnly,
  onSave,
  onDelete,
  allowLeadingZeros,
  allowEmpty = true,
  decimalScale = 0,
}) {
  const [spinner, setSpinner] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleDelete = async () => {
    try {
      setSpinner(true);
      await onDelete();
    } catch (error) {
      setValue(initialValue);
    } finally {
      setSpinner(false);
    }
  };

  const handleSave = async () => {
    try {
      setSpinner(true);
      await onSave(value);
    } catch (error) {
      setValue(initialValue);
    } finally {
      setEditMode(false);
      setSpinner(false);
    }
  };

  const handleCancel = () => {
    setEditMode(!editMode);
    setValue(initialValue);
  };

  if (readOnly) {
    return (
      <>
        <Form.Label className={`bo-common-input-label ${labelClassName}`}>{label}</Form.Label>
        <div className="w-100">
          <Form.Control className="bo-common-text-input" type="text" readOnly value={value} />
          <div className="bo-common-text-bottom-border" />
        </div>
      </>
    );
  }

  return (
    <>
      <Form.Label className={`bo-common-input-label ${labelClassName}`}>{label}</Form.Label>
      {editMode ? (
        <div className="bo-common-text-input-container">
          <div className="w-100">
            {type === 'numeric' ? (
              <NumericFormat
                type="text"
                className="bo-common-text-input"
                value={value}
                onValueChange={({ formattedValue, floatValue }) =>
                  setValue(allowLeadingZeros ? formattedValue : floatValue)
                }
                maxLength={maxLength}
                customInput={Form.Control}
                allowNegative={false}
                decimalScale={decimalScale}
                allowLeadingZeros={allowLeadingZeros}
                readOnly={spinner}
              />
            ) : (
              <Form.Control
                className="bo-common-text-input"
                type="text"
                value={value}
                onChange={e => setValue(e.target.value)}
                maxLength={maxLength}
                readOnly={spinner}
              />
            )}
            <div
              className={classNames(
                'bo-common-text-bottom-border',
                'bo-common-text-bottom-border-active',
              )}
            />
          </div>
          <div
            className={classNames(
              'bo-common-input-button-container',
              'bo-common-input-button-container-active',
            )}
          >
            {spinner ? (
              <div className="h-100">
                <CircularProgress color="inherit" size={16} />
              </div>
            ) : (
              <>
                <Button
                  className="bo-common-input-button-icon"
                  onClick={handleSave}
                  disabled={
                    (maxLength && value && String(value).length !== maxLength) ||
                    (maxLength && !value) ||
                    (!allowEmpty && (value === null || value === undefined))
                  }
                  key="save"
                >
                  <FontAwesomeIcon icon={faCheckCircle} color="#5946A4" size="1x" />
                </Button>
                <Button className="bo-common-input-button-icon" onClick={handleCancel} key="cancel">
                  <FontAwesomeIcon icon={faTimesCircle} color="#5946A4" size="1x" />
                </Button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="bo-common-text-input-container">
          <Button
            className="border-0 bg-transparent d-flex m-0 p-0 w-100 d-flex align-items-center"
            onClick={() => setEditMode(true)}
          >
            <div className="w-100">
              <Form.Control
                className="bo-common-text-input"
                type="text"
                readOnly
                value={value}
                onChange={e => setValue(e.target.value)}
              />
              <div className="bo-common-text-bottom-border" />
            </div>
            <div
              className={classNames(
                'bo-common-input-button-container',
                spinner && 'bo-common-input-button-container-active',
              )}
            >
              {spinner ? (
                <div className="h-100 text-dark">
                  <CircularProgress color="inherit" size={16} />
                </div>
              ) : (
                <div className="bo-common-input-button-icon">
                  <FontAwesomeIcon icon={faPencil} color="#5946A4" size="sm" />
                </div>
              )}
            </div>
          </Button>
          {onDelete && !spinner && (
            <div className="bo-common-trash-button-container">
              <Button className="bo-common-input-button-icon" onClick={handleDelete} key="delete">
                <FontAwesomeIcon icon={faTrashAlt} color="#5946A4" size="sm" />
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default CommonTextInput;
